import { useState } from 'react';

import { Button } from '../../../cdl/Button/Button';
import { GenericError } from '../../../common/Error/GenericError';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconChevronDown } from '../../../common/icons/cdl/ChevronDown';
import { IconChevronUp } from '../../../common/icons/cdl/ChevronUp';
import { Box } from '../../../common/ui/Box';
import { OfferModel } from '../../../offer/model/OfferModel';
import { OfferState } from '../../../offer/model/OfferState';
import { useOrderSupplierOnboardings } from '../../../supplierOnboarding/hooks/useOrderSupplierOnboardings';
import { useOfferSuggestionsStream } from '../../create-enquiry/hooks/useOfferSuggestionsStream';
import { useSpotAvailableAtPort } from '../../create-enquiry/hooks/useSpotAvailableAtPort';
import { OrderModel, OrderState } from '../../model/OrderModel';
import { useStateSet } from '../common/useStateSet';
import { DashedOfferCardOutline } from '../components/OfferCard/components/DashedOfferCardOutline';
import { OfferCardSkeleton } from '../components/OfferCard/components/OfferCardSkeleton';
import { OfferCard } from '../components/OfferCard/OfferCard';
import { useDefaultContractReference } from '../hooks/useDefaultContractReference';
import { useOrderOffers } from '../hooks/useOrderOffers';

import { DefaultContractReferenceCard } from './DefaultContractReferenceCard';
import { getCheapestValidOffer } from './helpers/getCheapestValidOffer';
import { isValidOfferWithPrice } from './helpers/isValidOfferWithPrice';

interface OfferListProps {
    order: OrderModel;
}

const isDeclinedSpotOffer = (offer: OfferModel) => offer.isSpot() && offer.state === OfferState.ENQUIRY_DECLINED;

export const OfferList = ({ order }: OfferListProps) => {
    const offerIdSetState = useStateSet();
    const [showDeclinedSpotOffers, setShowDeclinedSpotOffers] = useState(false);
    const expandedOfferIds = offerIdSetState.value;
    const offersQuery = useOrderOffers(order);
    const offerSuggestionsQuery = useOfferSuggestionsStream(order.id);
    const onboardingsQuery = useOrderSupplierOnboardings(order.id);
    const role = useRole();
    const spotAvailabilityQuery = useSpotAvailableAtPort(
        order.port?.country?.code,
        order.testOrder,
        !role.isSuperAdmin()
    );
    const isSpotOnlyOrder = order.isSpot() && order.receiverSupplierIds.length === 0;
    const { defaultContractReference, isPending: isDefaultContractReferencePending } =
        useDefaultContractReference(order);
    const defaultContractReferenceCard = (
        <DefaultContractReferenceCard price={defaultContractReference} isPending={isDefaultContractReferencePending} />
    );

    if (offersQuery.isError) {
        return <GenericError />;
    }

    const onOfferCardClick = (offerId: string) => {
        if (expandedOfferIds.has(offerId)) {
            offerIdSetState.remove(offerId);
        } else {
            offerIdSetState.add(offerId);
        }
    };

    if (offersQuery.data?.length) {
        const declinedSpotOffers = offersQuery.data.filter(isDeclinedSpotOffer);
        const visibleOffers = offersQuery.data.filter((offer) => !isDeclinedSpotOffer(offer));
        const hasVisibleOffers = visibleOffers.length > 0;
        const showSpotWaitingPlaceholder =
            order.isOpen() && (order.isSpot() || (declinedSpotOffers.length > 0 && !hasVisibleOffers));
        const difference = order.receiverSupplierIds.length - visibleOffers.length;
        const openOffers = visibleOffers.filter((offer) => offer.isEnquiry());
        const isSpotAvailable = role.isSpotAllowed(order.customerId) && spotAvailabilityQuery.data?.available === true;

        const cheapestOffer = getCheapestValidOffer(visibleOffers);
        const validOffersWithPriceCount = visibleOffers.filter(isValidOfferWithPrice).length;

        const isBestLiterPrice = (offer: OfferModel) => {
            if (!cheapestOffer?.ppl) {
                return false;
            }

            if (validOffersWithPriceCount === 1 || !isValidOfferWithPrice(offer)) {
                return false;
            }

            return offer.ppl?.value === cheapestOffer.ppl.value;
        };

        const isRecommended = (offer: OfferModel) => {
            return offerSuggestionsQuery.finalResult?.selectedOfferId === offer.id;
        };

        const onboardingForOffer = (offer: OfferModel) =>
            onboardingsQuery.data?.find((onboarding) => onboarding.requestForQuoteId === offer.requestForQuoteId) ??
            null;

        const DeclinedSpotOfferToggleIcon = showDeclinedSpotOffers ? IconChevronUp : IconChevronDown;
        let declinedSpotOfferToggleLabel = translate('order.detail.hideDeclinedSpotOffers');
        if (!showDeclinedSpotOffers && declinedSpotOffers.length === 1) {
            declinedSpotOfferToggleLabel = translate('order.detail.showDeclinedSpotOffer');
        }
        if (!showDeclinedSpotOffers && declinedSpotOffers.length > 1) {
            declinedSpotOfferToggleLabel = translate('order.detail.showDeclinedSpotOffers', {
                count: declinedSpotOffers.length.toString(),
            });
        }
        const declinedSpotOfferToggle = declinedSpotOffers.length ? (
            <Box display="flex">
                <Button
                    type="button"
                    emphasis="low"
                    size="small"
                    onClick={() => setShowDeclinedSpotOffers((currentValue) => !currentValue)}
                    TrailingIcon={DeclinedSpotOfferToggleIcon}
                >
                    {declinedSpotOfferToggleLabel}
                </Button>
            </Box>
        ) : null;

        return (
            <Box display="grid" rowGap={16}>
                {defaultContractReferenceCard}
                {visibleOffers.map((offer) => (
                    <OfferCard
                        key={offer.id}
                        offer={offer}
                        order={order}
                        isLastOpenOffer={openOffers.length === 1 && openOffers[0].id === offer.id}
                        isSpotAvailable={isSpotAvailable}
                        onClick={() => onOfferCardClick(offer.id)}
                        expanded={expandedOfferIds.has(offer.id)}
                        isBestLiterPrice={isBestLiterPrice(offer)}
                        isRecommended={isRecommended(offer)}
                        onboarding={onboardingForOffer(offer)}
                    />
                ))}
                {difference > 0
                    ? Array.from({ length: difference }).map((_, index) => <OfferCardSkeleton key={index} />)
                    : null}
                {showSpotWaitingPlaceholder ? (
                    <DashedOfferCardOutline>{translate('order.quotes.moretocome')}</DashedOfferCardOutline>
                ) : null}
                {declinedSpotOfferToggle}
                {showDeclinedSpotOffers
                    ? declinedSpotOffers.map((offer) => (
                          <OfferCard
                              key={offer.id}
                              offer={offer}
                              order={order}
                              isLastOpenOffer={false}
                              isSpotAvailable={isSpotAvailable}
                              onClick={() => onOfferCardClick(offer.id)}
                              expanded={expandedOfferIds.has(offer.id)}
                              isBestLiterPrice={false}
                              isRecommended={false}
                              onboarding={onboardingForOffer(offer)}
                          />
                      ))
                    : null}
            </Box>
        );
    }

    if (order.isDraft()) {
        return <DashedOfferCardOutline>{translate('order.quotes.draft')}</DashedOfferCardOutline>;
    }

    if (isSpotOnlyOrder) {
        if (order.isOpen()) {
            return (
                <Box display="grid" rowGap={16}>
                    {defaultContractReferenceCard}
                    <DashedOfferCardOutline>{translate('order.quotes.moretocome')}</DashedOfferCardOutline>
                </Box>
            );
        }

        if (order.state === OrderState.CANCELED) {
            return (
                <DashedOfferCardOutline>
                    {translate('order.quotes.canceledSpotEnquiryWithoutOffers')}
                </DashedOfferCardOutline>
            );
        }
    }

    return (
        <Box display="grid" rowGap={16}>
            {defaultContractReferenceCard}
            {order.receiverSupplierIds?.map((id) => {
                return <OfferCardSkeleton key={id} />;
            })}
        </Box>
    );
};
